import CoreApplicationStateModel from '@/core/model/CoreApplicationStateModel';

// used for encoding when sharing state on social-media
import PersonComparisonModel from '@/app/views/person/model/personComparisonModel';
import RegionalComparisonModel from '@/app/views/regional/model/regionalComparisonModel';
import ContentModel from './contentModel';

export default class AppStateModel extends CoreApplicationStateModel {

    private static _birthday:string;
    private static _gender:string;
    private static _estimated_age:string;
    private static _birthdayMap:any;
    private static _canJumpToStep:boolean = false;

    static set birthday(value) {
        AppStateModel._birthday = value;
    }

    static get birthday() {
        return AppStateModel._birthday;
    }

    static set gender(value) {
        AppStateModel._gender = value;
    }

    static get gender() {
        return AppStateModel._gender;
    }

    static get birthdayMap() {
        return AppStateModel._birthdayMap;
    }

    static set birthdayMap(value) {
        AppStateModel._birthdayMap = value;
    }

    static getResultsByBirthdayAndGender(birthday = AppStateModel.birthday, gender = AppStateModel.gender, resultMap = AppStateModel.birthdayMap) {

        if (!birthday || !gender) {
            return {};
        }

        return AppStateModel.correctResult(birthday, gender, resultMap);
    }

    static correctResult(birthday, gender, resultMap) {
        let correctedBirthday = parseInt(birthday)-ContentModel.instance.getCommonData().correction.years_to_add;
        let result = Object.assign({}, resultMap[correctedBirthday][gender]);
        return result;
    }

    static set estimated_age(value) {
        AppStateModel._estimated_age = value;
    }

    static get estimated_age() {
        return AppStateModel._estimated_age;
    }

    static enableJumpToStepOnce() {
        AppStateModel._canJumpToStep = true;
    }

    static canJumpToStepOnce() {
        // determine if a user should jump straight to result-sections and etc, ONCE.
        let allow = false;
        if (AppStateModel._canJumpToStep) {
            allow = true;
            AppStateModel._canJumpToStep = false;
        }
        return allow;
    }

    static getStateQueryString(encoded=true) {
        // this function composes a encoded state parameter for ALL models
        // "ler" + "x" (x = state var) is the prepending format we are using "ler" = lebenserwartungsrechner.

        let state = {
            t: AppStateModel.themeParam,
            ler_v: AppStateModel.currentViewID,
            ler_b: AppStateModel.birthday,
            ler_g: AppStateModel.gender,
            ler_e: AppStateModel.estimated_age,
            ler_s: RegionalComparisonModel.stepContext,
            // cohort-table does not need specifics for sharing
            // person-comparison is handled separately below
            ler_pln: '',
            ler_plb: '',
            ler_plg: '',
            ler_ple: '',
            ler_prn: '',
            ler_prb: '',
            ler_prg: '',
            ler_pre: ''
        };

        // conditional on the premise that PersonalComparisonModel contain the expect people-object
        if (PersonComparisonModel.people && Object.prototype.hasOwnProperty.call(PersonComparisonModel.people, 'right') && Object.prototype.hasOwnProperty.call(PersonComparisonModel.people, 'left')) {
            state.ler_pln = PersonComparisonModel.people.left.name;
            state.ler_plb = PersonComparisonModel.people.left.birthyear;
            state.ler_plg = PersonComparisonModel.people.left.gender;
            state.ler_ple = PersonComparisonModel.people.left.estimated_age;
            state.ler_prn = PersonComparisonModel.people.right.name;
            state.ler_prb = PersonComparisonModel.people.right.birthyear;
            state.ler_prg = PersonComparisonModel.people.right.gender;
            state.ler_pre = PersonComparisonModel.people.right.estimated_age
        }

        let params = '';
        for (let key in state) {
            // only bother including values that are present in our models (reduces length when able)
            if (state[key] !== undefined) {

                // add param-separation after the first
                if (params.length !== 0) params += '&';

                params += key + '=' + state[key];
            }
        }

        if (encoded) {
            // encode our state so we can nest it in sharing requests via query-strings
            return encodeURIComponent(params);
        } else {
            return params;
        }
    }

    static decodeStateQueryString(paramsURL) {
        // decode state to model as found in query-string
        let decoded = decodeURIComponent(paramsURL);

        // each query-param MUST be separated by "&" character
        let parsed = decoded.split('&');
        let canJumpToStep = parsed.length > 3;  // naive fix, as we now have theme as param, and it alone is not enough to jump ahead

        // decides where in models to put state values:
        const personComparisonParams = ["ler_pln", "ler_plb", "ler_plg", "ler_ple", "ler_prn", "ler_prb", "ler_prg", "ler_pre"]
        let funnelStateToModels = (key, value) => {
            if (!PersonComparisonModel.people && personComparisonParams.includes(key)) PersonComparisonModel.people = {left: {id: 'left'}, right: {id: 'right'}};

            switch (key) {
                case 'ler_b':
                    AppStateModel.birthday = value;
                    break;
                case 'ler_g':
                    AppStateModel.gender = value;
                    break;
                case 'ler_e':
                    AppStateModel.estimated_age = value;
                    break;
                case 'ler_s':
                    RegionalComparisonModel.stepContext = value;
                    break;
                case 'ler_pln':
                    PersonComparisonModel.people.left.name = value;
                    break;
                case 'ler_plb':
                    PersonComparisonModel.people.left.birthyear = value;
                    break;
                case 'ler_plg':
                    PersonComparisonModel.people.left.gender = value;
                    break;
                case 'ler_ple':
                    PersonComparisonModel.people.left.estimated_age = value;
                    break;
                case 'ler_prn':
                    PersonComparisonModel.people.right.name  = value;
                    break;
                case 'ler_prb':
                    PersonComparisonModel.people.right.birthyear  = value;
                    break;
                case 'ler_prg':
                    PersonComparisonModel.people.right.gender  = value;
                    break;
                case 'ler_pre':
                    PersonComparisonModel.people.right.estimated_age  = value;
                    break;
                case 't':
                    AppStateModel.themeParam = value;
                    break;
            }
        };

        // split and spread each param, from: ["key=value", "key=value", etc]
        for (let param of parsed) {
            const [key, value] = param.split('=');
            funnelStateToModels(key, value);
        }

        return canJumpToStep
    }

    // this is ONLY here to make IE 10 work, which does not find it's way to the parent-class without help.
    static get content() {
        return super.content;
    }

    static set currentViewID(viewID) {
        super.currentViewID = viewID;
    }

    static get currentViewID() {
        return super.currentViewID;
    }
}
